import { marked, Marked } from "marked";
import DOMPurify from "isomorphic-dompurify";
import { isLiveAppLink, toDeployedAppLink } from "./deployed-apps";

/**
 * Render markdown to sanitized HTML, safe to pass to dangerouslySetInnerHTML.
 *
 * marked passes raw HTML in the source straight through — a markdown string
 * can carry <script>, <img onerror>, javascript: URLs, etc. Every caller
 * renders content the agent can be steered to author (kanban card
 * descriptions, workspace files), so the output runs through DOMPurify,
 * which strips anything executable, before it reaches the DOM.
 *
 * This is the single place markdown → HTML happens in the web app; keep it
 * that way so the sanitization guarantee has one home (and one test).
 *
 * `linkHref`, when given, chooses where each markdown link points (see
 * linkRewritingMarked).
 */
export const renderMarkdown = (text: string, options: { linkHref?: (href: string) => string | null } = {}): string => {
  const { linkHref } = options;
  const html = linkHref === undefined
    ? marked.parse(text, { async: false, breaks: true })
    : linkRewritingMarked(linkHref).parse(text, { async: false, breaks: true });
  return DOMPurify.sanitize(html);
};

const escapeAttribute = (value: string): string =>
  value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");

/**
 * A parser whose markdown links go where `linkHref` says — for documents whose
 * relative links mean something other than "relative to this page" (a
 * workspace file links its neighbours on disk). `linkHref` answering null
 * leaves the link to marked's own rendering. Isolated from the `marked`
 * singleton like the chat parser below, and sanitized after like everything
 * else here, so the rewritten href is held to the same rules.
 */
const linkRewritingMarked = (linkHref: (href: string) => string | null): Marked => new Marked({
  renderer: {
    link({ href, title, tokens }) {
      const target = linkHref(href);
      if (target === null) return false;
      const titleAttribute = title ? ` title="${escapeAttribute(title)}"` : "";
      return `<a href="${escapeAttribute(target)}"${titleAttribute}>${this.parser.parseInline(tokens)}</a>`;
    },
  },
});

/**
 * Inline-only variant — emphasis, links, code spans; no block elements
 * (headings, lists, code fences stay literal text). For compact previews
 * like kanban card tiles where block layout would break a line clamp.
 * Sanitized the same way as renderMarkdown.
 */
export const renderMarkdownInline = (text: string): string =>
  DOMPurify.sanitize(marked.parseInline(text, { async: false, breaks: true }));

/**
 * Is this link to OUR OWN site — a relative path, or an absolute URL on the
 * given origin? Those stay in the tab: the agent is pointing the person at a
 * page of the product (the organization page, a run to decide on, a desk), and
 * a second tab of the same app is the wrong shape for that. Everything else is
 * outbound and opens beside the conversation.
 *
 * Protocol-relative (`//host`) is absolute and counts as outbound. The origin
 * is compared as a prefix up to a path boundary, so `https://squidler.io.evil`
 * does not pass for `https://squidler.io`.
 */
export const isSameSiteLink = (href: string, siteOrigin: string): boolean => {
  // A deployed app is served under our origin but is not a page of the
  // product: opened in the tab, it fills an installed app with no way back to
  // the chat (report d4058acf). See chatLinkHtml.
  if (isLiveAppLink(href)) return false;
  if (href.startsWith("/") && !href.startsWith("//")) return true;
  if (siteOrigin === "") return false;
  return href === siteOrigin || href.startsWith(`${siteOrigin}/`) || href.startsWith(`${siteOrigin}?`) || href.startsWith(`${siteOrigin}#`);
};

/**
 * Chat variant — same sanitization, but outbound links open in a new tab so
 * the user doesn't navigate away from the conversation, while links to our own
 * site (`siteOrigin`, the app's base URL) navigate in place. Chat content
 * (assistant output, which the agent can be steered to author, and echoed user
 * text) is as attacker-influenceable as any other sink, so it must go through
 * DOMPurify too rather than straight from `marked.parse` into
 * `dangerouslySetInnerHTML`.
 *
 * The isolated `Marked` instance keeps the link renderer from leaking into the
 * shared `marked` singleton used by renderMarkdown. DOMPurify still runs
 * after, so a `javascript:` href or injected handler is stripped regardless of
 * what the renderer emits. `siteOrigin` is runtime server config handed down
 * through a provider — never `window.location` — so server and client render
 * the same markup and hydration has nothing to disagree about.
 */
const chatMarkedFor = (siteOrigin: string, appLinkOrigin: string): Marked => new Marked({
  breaks: true,
  renderer: {
    link({ href, text }) {
      return chatLinkHtml(href, text, siteOrigin, appLinkOrigin);
    },
  },
});

/**
 * One chat link. A live deployed app goes out through the app-link host
 * (`toDeployedAppLink`), as the sidebar's and the dashboard's links do, so an
 * installed web app opens it in a browser and stays on the chat — the reason
 * that host exists (apps/web/CLAUDE.md, "Deployed-app links").
 */
export const chatLinkHtml = (href: string, text: string, siteOrigin: string, appLinkOrigin: string): string => {
  if (isSameSiteLink(href, siteOrigin)) return `<a href="${href}">${text}</a>`;
  const target = toDeployedAppLink(href, appLinkOrigin);
  return `<a href="${target}" target="_blank" rel="noopener noreferrer">${text}</a>`;
};

/** The parser for the origins last asked for. They are server config, so this
 *  is one parser in practice; different origins (a test) just rebuild. */
const chatMarkedCache: { key: string; parser: Marked } = { key: "\n", parser: chatMarkedFor("", "") };

export const renderChatMarkdown = (text: string, siteOrigin = "", appLinkOrigin = ""): string => {
  const key = `${siteOrigin}\n${appLinkOrigin}`;
  if (chatMarkedCache.key !== key) {
    chatMarkedCache.key = key;
    chatMarkedCache.parser = chatMarkedFor(siteOrigin, appLinkOrigin);
  }
  return DOMPurify.sanitize(chatMarkedCache.parser.parse(text, { async: false }), { ADD_ATTR: ["target"] });
};
